import * as React from 'react'
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import BuildRoundedIcon from '@mui/icons-material/BuildRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'
import UnitIconFrame from './UnitIconFrame'

const PLUGIN_KEY = 'auto_recruit_tools'
const TOOL_BUILD_PICKER_FAVORITES_KEY = 'toolBuildPickerFavorites'
const DEFAULT_INTERVAL_MINUTES = '30'
const DEFAULT_AMOUNT_PER_SLOT = '80'
const SLOT_COUNT = 5

const WORKSHOP_DEFINITIONS = Object.freeze([
    {
        key: 'siege',
        title: 'Siege Queue',
        shortLabel: 'Siege',
        description: 'Control the slot order used by siege workshops.',
        icon: HandymanRoundedIcon
    },
    {
        key: 'defense',
        title: 'Defense Queue',
        shortLabel: 'Defense',
        description: 'Control the slot order used by defense workshops.',
        icon: ShieldRoundedIcon
    }
])

export const TOOL_BUILD_KINGDOM_DEFINITIONS = Object.freeze([
    {
        key: 'main',
        title: 'Main Kingdom',
        shortLabel: 'Main',
        description: 'Use these workshop queues in the Great Empire.'
    },
    {
        key: 'ice',
        title: 'Ice Kingdom',
        shortLabel: 'Ice',
        description: 'Use these workshop queues in Everwinter Glacier.'
    },
    {
        key: 'desert',
        title: 'Desert Kingdom',
        shortLabel: 'Desert',
        description: 'Use these workshop queues in Burning Sands.'
    },
    {
        key: 'fire',
        title: 'Fire Kingdom',
        shortLabel: 'Fire',
        description: 'Use these workshop queues in Fire Peaks.'
    }
])

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const searchValue = value =>
    String(value ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()

const levelValue = (statLine, fallback = '') =>
    String(statLine ?? '').match(/Level\s+([0-9]+)/i)?.[1] ?? String(fallback ?? '')

const normalizePositiveNumberText = (value, fallback = '') => {
    if ([undefined, null, ''].includes(value))
        return fallback

    const parsed = Number.parseInt(String(value), 10)
    return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : fallback
}

const normalizeSelectionOption = option => {
    const value = String(option?.value ?? option?.id ?? '').trim()
    const label = String(option?.label ?? value)
    const statLine = String(option?.statLine ?? 'Tool')

    if (option && typeof option === 'object' && !Array.isArray(option)) {
        return {
            id: value,
            key: `tool:${value}`,
            label,
            value,
            itemType: 'tool',
            statLine,
            level: levelValue(statLine, option?.level),
            iconUrl: String(option?.iconUrl ?? ''),
            assetKey: String(option?.assetKey ?? ''),
            searchText: String(option?.searchText ?? `${label} ${value} ${statLine}`)
        }
    }

    return {
        id: value,
        key: `tool:${value}`,
        label,
        value,
        itemType: 'tool',
        statLine,
        level: levelValue(statLine),
        iconUrl: '',
        assetKey: '',
        searchText: `${label} ${value} ${statLine}`
    }
}

const buildFallbackOption = value => normalizeSelectionOption({ value, label: String(value ?? ''), statLine: 'Tool' })

const mergeToolEntries = (...groups) => {
    const nextEntries = []
    const seenKeys = new Set()

    groups.flat().forEach(entry => {
        const normalizedEntry = normalizeSelectionOption(entry)
        if (!normalizedEntry.value || seenKeys.has(normalizedEntry.key))
            return

        seenKeys.add(normalizedEntry.key)
        nextEntries.push(normalizedEntry)
    })

    return nextEntries
}

export const buildToolPickerEntriesByWorkshop = pluginDefinition =>
    Object.fromEntries(
        WORKSHOP_DEFINITIONS.map(workshop => {
            const relevantOptions = (pluginDefinition?.pluginOptions ?? []).filter(option => {
                const optionKey = String(option?.key ?? '').toLowerCase()
                return optionKey.startsWith(workshop.key.toLowerCase()) && optionKey.includes('tool')
            })

            return [
                workshop.key,
                mergeToolEntries(
                    ...relevantOptions.map(option => Array.isArray(option?.selection) ? option.selection : [])
                )
            ]
        })
    )

export const withSavedToolFallback = (entries, savedValue) => {
    const normalizedSavedValue = String(savedValue ?? '').trim()
    if (!normalizedSavedValue)
        return entries

    return entries.some(option => option.value === normalizedSavedValue) ?
        entries :
        [buildFallbackOption(normalizedSavedValue), ...entries]
}

export const resolveToolSelection = (rawValue, entries, savedValue = '') => {
    const normalizedValue = searchValue(rawValue)
    if (!normalizedValue)
        return null

    const matchedEntry = (Array.isArray(entries) ? entries : []).find(option =>
        [option?.value, option?.id, option?.label]
            .filter(Boolean)
            .some(candidate => searchValue(candidate) === normalizedValue)
    )

    if (matchedEntry)
        return matchedEntry

    const normalizedSavedValue = String(savedValue ?? '').trim()
    return normalizedSavedValue && searchValue(normalizedSavedValue) === normalizedValue ?
        buildFallbackOption(normalizedSavedValue) :
        null
}

export const getNextToolSlotForToggle = (slot, nextEnabled) => {
    if (!nextEnabled)
        return {
            ...slot,
            enabled: false,
            tool: ''
        }

    if (String(slot?.tool ?? '').trim())
        return {
            ...slot,
            enabled: true
        }

    return null
}

const normalizeToolSlotState = slot => {
    const tool = String(slot?.tool ?? slot?.value ?? '').trim()
    const slotEnabled = Boolean(slot?.enabled ?? tool)

    return {
        enabled: Boolean(slotEnabled && tool),
        tool: slotEnabled ? tool : '',
        amount: normalizePositiveNumberText(slot?.amount, DEFAULT_AMOUNT_PER_SLOT)
    }
}

const buildEmptyWorkshopSlots = () =>
    Array.from({ length: SLOT_COUNT }, () => ({
        enabled: false,
        tool: '',
        amount: DEFAULT_AMOUNT_PER_SLOT
    }))

const readLegacyWorkshopSlots = (pluginState, workshopKey) =>
    Array.from({ length: SLOT_COUNT }, (_, index) => {
        const slotIndex = index + 1
        const rawTool = String(
            pluginState?.[`${workshopKey}Slot${slotIndex}Tool`] ??
            pluginState?.[`${workshopKey}Slot${slotIndex}`] ??
            ''
        ).trim()

        return {
            enabled: Boolean(rawTool),
            tool: rawTool,
            amount: normalizePositiveNumberText(
                pluginState?.[`${workshopKey}Slot${slotIndex}Amount`],
                DEFAULT_AMOUNT_PER_SLOT
            )
        }
    })

const readKingdomWorkshopSlots = (pluginState, kingdomKey, workshopKey, legacySlotsByWorkshop) => {
    const kingdomState = pluginState?.kingdoms?.[kingdomKey]
    const slots = kingdomState?.workshops?.[workshopKey]?.slots

    if (Array.isArray(slots))
        return Array.from({ length: SLOT_COUNT }, (_, index) =>
            normalizeToolSlotState(slots[index] ?? {
                enabled: false,
                tool: '',
                amount: DEFAULT_AMOUNT_PER_SLOT
            })
        )

    return (legacySlotsByWorkshop[workshopKey] ?? buildEmptyWorkshopSlots()).map(slot => ({ ...slot }))
}

const normalizeNextKingdomConfig = (kingdomKey, sourceConfig, fallbackWorkshops) => {
    const sourceKingdom = sourceConfig?.kingdoms?.[kingdomKey]
    const workshopsSource = sourceKingdom?.workshops ?? fallbackWorkshops ?? {}

    return {
        enabled: typeof sourceKingdom?.enabled === 'boolean' ? sourceKingdom.enabled : true,
        workshops: Object.fromEntries(
            WORKSHOP_DEFINITIONS.map(workshop => {
                const slots = Array.isArray(workshopsSource?.[workshop.key]?.slots) ?
                    workshopsSource[workshop.key].slots :
                    buildEmptyWorkshopSlots()

                return [
                    workshop.key,
                    {
                        slots: Array.from({ length: SLOT_COUNT }, (_, index) =>
                            normalizeToolSlotState(slots[index] ?? {
                                enabled: false,
                                tool: '',
                                amount: DEFAULT_AMOUNT_PER_SLOT
                            })
                        )
                    }
                ]
            })
        )
    }
}

export const buildToolBuildPluginState = (userPlugins, nextConfig) => {
    const nextPlugins = cloneValue(userPlugins ?? {})
    let hasEnabledSlots = false

    nextPlugins[PLUGIN_KEY] ??= {}
    nextPlugins[PLUGIN_KEY].intervalMinutes = normalizePositiveNumberText(nextConfig.intervalMinutes, DEFAULT_INTERVAL_MINUTES)
    nextPlugins[PLUGIN_KEY].askAllianceHelp = Boolean(nextConfig.askAllianceHelp)

    const normalizedKingdoms = Object.fromEntries(
        TOOL_BUILD_KINGDOM_DEFINITIONS.map(kingdom => {
            const normalizedKingdom = normalizeNextKingdomConfig(kingdom.key, nextConfig, nextConfig?.workshops)

            Object.values(normalizedKingdom.workshops).forEach(workshop => {
                workshop.slots.forEach(slot => {
                    if (normalizedKingdom.enabled && slot.enabled && slot.tool)
                        hasEnabledSlots = true
                })
            })

            return [kingdom.key, normalizedKingdom]
        })
    )

    nextPlugins[PLUGIN_KEY].kingdoms = normalizedKingdoms

    const legacySource = normalizedKingdoms.main ?? Object.values(normalizedKingdoms)[0]
    WORKSHOP_DEFINITIONS.forEach(workshop => {
        legacySource.workshops[workshop.key].slots.forEach((slot, index) => {
            const slotIndex = index + 1
            nextPlugins[PLUGIN_KEY][`${workshop.key}Slot${slotIndex}Tool`] = slot.enabled ? String(slot.tool ?? '').trim() : ''
            nextPlugins[PLUGIN_KEY][`${workshop.key}Slot${slotIndex}Amount`] = normalizePositiveNumberText(slot.amount, DEFAULT_AMOUNT_PER_SLOT)
        })
    })

    nextPlugins[PLUGIN_KEY].state = hasEnabledSlots

    return nextPlugins
}

const loadToolPickerFavorites = () => {
    if (typeof window === 'undefined')
        return []

    try {
        const parsed = JSON.parse(window.localStorage.getItem(TOOL_BUILD_PICKER_FAVORITES_KEY) ?? '[]')
        return Array.isArray(parsed) ? parsed.map(value => String(value)).filter(Boolean) : []
    }
    catch {
        return []
    }
}

const saveToolPickerFavorites = favoriteKeys => {
    if (typeof window === 'undefined')
        return

    try {
        window.localStorage.setItem(TOOL_BUILD_PICKER_FAVORITES_KEY, JSON.stringify(favoriteKeys))
    }
    catch { }
}

const FIELD_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        minHeight: 42,
        borderRadius: '10px',
        backgroundColor: 'rgba(24, 36, 57, 0.76)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

const PANEL_CARD_SX = {
    p: { xs: 2, md: 2.5 },
    borderRadius: '18px',
    border: '1px solid rgba(129, 146, 178, 0.14)',
    backgroundColor: 'rgba(20, 31, 52, 0.5)'
}

function Helper({ children }) {
    return (
        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 0.78 }}>
            <InfoOutlinedIcon sx={{ fontSize: 12.5, color: '#95a5c4' }} />
            <Typography sx={{ fontSize: '0.885rem', color: '#95a5c4', lineHeight: 1.35 }}>{children}</Typography>
        </Stack>
    )
}

function FieldTitle({ children }) {
    return (
        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
            {children}
        </Typography>
    )
}

function StyledSwitch({ checked, onChange, disabled = false }) {
    return (
        <Switch
            checked={checked}
            disabled={disabled}
            onChange={event => onChange(event.target.checked)}
            sx={{
                width: 48,
                height: 29,
                p: 0,
                '& .MuiSwitch-switchBase': {
                    p: '2.5px',
                    '&.Mui-checked': {
                        transform: 'translateX(18px)',
                        color: '#ffffff'
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#2f7cff',
                        opacity: 1
                    }
                },
                '& .MuiSwitch-thumb': {
                    width: 24,
                    height: 24,
                    boxShadow: 'none'
                },
                '& .MuiSwitch-track': {
                    opacity: 1,
                    borderRadius: '999px',
                    backgroundColor: 'rgba(111, 123, 143, 0.9)'
                }
            }}
        />
    )
}

function ToggleField({ checked, onChange, label, helper, disabled = false }) {
    return (
        <Box sx={{ minHeight: helper ? 56 : 34 }}>
            <Stack direction="row" spacing={1.1} alignItems="center" sx={{ pt: helper ? 0.25 : 0 }}>
                <StyledSwitch checked={checked} onChange={onChange} disabled={disabled} />
                <Typography sx={{ color: '#f5f7ff', fontSize: '0.995rem', fontWeight: 800, letterSpacing: '-0.01em' }}>
                    {label}
                </Typography>
            </Stack>
            {helper ? <Helper>{helper}</Helper> : null}
        </Box>
    )
}

function SectionHeading({ children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ mb: 2.2 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>{children}</Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: 'rgba(129, 146, 178, 0.22)' }} />
        </Stack>
    )
}

function PickerFilterButton({ icon, label, count, active, onClick }) {
    return (
        <Button
            variant="outlined"
            onClick={onClick}
            startIcon={icon}
            sx={{
                minHeight: 36,
                px: 1.35,
                borderRadius: '10px',
                borderColor: active ? 'rgba(112, 136, 174, 0.56)' : 'rgba(112, 136, 174, 0.34)',
                backgroundColor: active ? 'rgba(74, 91, 120, 0.72)' : 'rgba(42, 54, 78, 0.56)',
                color: '#eef4ff',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.96rem',
                gap: 0.8,
                '&:hover': {
                    borderColor: 'rgba(112, 136, 174, 0.58)',
                    backgroundColor: active ? 'rgba(82, 98, 127, 0.78)' : 'rgba(50, 63, 88, 0.68)'
                }
            }}
        >
            <Box component="span">{label}</Box>
            <Box
                component="span"
                sx={{
                    px: 0.75,
                    minWidth: 34,
                    minHeight: 24,
                    borderRadius: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: '#aebad3',
                    fontWeight: 800,
                    fontSize: '0.95rem'
                }}
            >
                {count}
            </Box>
        </Button>
    )
}

function QueueSlotField({
    label,
    helper,
    selectedOption,
    selectedLabel,
    amount,
    onOpen,
    disabled,
    t
}) {
    return (
        <Box>
            <FieldTitle>{label}</FieldTitle>
            <TextField
                fullWidth
                value={selectedLabel}
                placeholder={t('Pick tool')}
                onClick={disabled ? undefined : onOpen}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={disabled ? undefined : onOpen} edge="end" sx={{ color: '#d9e4fb' }} disabled={disabled}>
                                <SearchRoundedIcon />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
                sx={FIELD_INPUT_SX}
                disabled={disabled}
            />
            {selectedLabel ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} useFlexGap flexWrap="wrap">
                    <UnitIconFrame iconUrl={selectedOption?.iconUrl} itemType="tool" amount={amount ? `x${amount}` : ''} size={36} />
                    <Chip label={t('Tool')} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.12)', color: '#c3d2eb' }} />
                    <Chip label={`${t('Qty')} ${amount}`} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.12)', color: '#8fa3c8' }} />
                </Stack>
            ) : null}
            <Helper>{helper}</Helper>
        </Box>
    )
}

function ToolPickerDialog({ open, entries, onClose, onSelect, appLanguage = 'en' }) {
    const t = useAutoTranslate(appLanguage, [
        'Troop & Tool Picker',
        'Search by tool name...',
        'Favorites',
        'Clear',
        'All',
        'Tools',
        'results',
        'Tool',
        'No results found',
        'Click to select - Favorite to bookmark'
    ])
    const [query, setQuery] = React.useState('')
    const [filterValue, setFilterValue] = React.useState('all')
    const [favoritesOnly, setFavoritesOnly] = React.useState(false)
    const [favoriteKeys, setFavoriteKeys] = React.useState(() => loadToolPickerFavorites())
    const favoriteKeySet = React.useMemo(() => new Set(favoriteKeys), [favoriteKeys])

    React.useEffect(() => {
        if (!open)
            return

        setQuery('')
        setFilterValue('all')
        setFavoritesOnly(false)
    }, [open])

    React.useEffect(() => {
        saveToolPickerFavorites(favoriteKeys)
    }, [favoriteKeys])

    const counts = React.useMemo(() => ({
        all: entries.length,
        tools: entries.length
    }), [entries])

    const filteredEntries = React.useMemo(() => {
        const normalizedQuery = searchValue(query)

        return entries
            .filter(entry => {
                if (filterValue === 'tools' && entry.itemType !== 'tool')
                    return false
                if (favoritesOnly && !favoriteKeySet.has(entry.key))
                    return false
                if (!normalizedQuery)
                    return true

                return searchValue(`${entry.label} ${entry.id} ${entry.searchText}`).includes(normalizedQuery)
            })
            .sort((left, right) => {
                const leftFavorite = favoriteKeySet.has(left.key)
                const rightFavorite = favoriteKeySet.has(right.key)
                if (leftFavorite !== rightFavorite)
                    return leftFavorite ? -1 : 1

                return String(left.label ?? '').localeCompare(String(right.label ?? ''))
            })
    }, [entries, favoriteKeySet, favoritesOnly, filterValue, query])

    const toggleFavorite = itemKey => {
        setFavoriteKeys(currentKeys =>
            currentKeys.includes(itemKey) ?
                currentKeys.filter(currentKey => currentKey !== itemKey) :
                [...currentKeys, itemKey]
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(calc(100vw - 20px), 566px)',
                    maxWidth: '566px',
                    minHeight: '724px',
                    maxHeight: 'min(calc(100vh - 20px), 724px)',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    background: 'linear-gradient(180deg, rgba(39,51,74,0.98) 0%, rgba(31,43,66,0.98) 100%)',
                    border: '1px solid rgba(122, 143, 180, 0.32)',
                    boxShadow: '0 26px 60px rgba(0,0,0,0.42)'
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(3, 8, 18, 0.68)'
                }
            }}
        >
            <DialogTitle sx={{ px: 2.2, pt: 2.05, pb: 1.25 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <SearchRoundedIcon sx={{ fontSize: 28, color: '#f4f7ff' }} />
                        <Typography sx={{ fontSize: '1.16rem', fontWeight: 800, color: '#f5f7ff' }}>
                            {t('Troop & Tool Picker')}
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} sx={{ color: '#9aa7c2', width: 30, height: 30 }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ px: 2.2, pt: 0, pb: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Stack direction="row" spacing={1.1} sx={{ mb: 1.5 }}>
                    <TextField
                        fullWidth
                        placeholder={t('Search by tool name...')}
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                minHeight: 42,
                                borderRadius: '10px',
                                backgroundColor: 'rgba(28, 41, 65, 0.9)',
                                '& fieldset': {
                                    borderColor: 'rgba(57, 121, 248, 0.95)',
                                    borderWidth: '2px'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(74, 134, 255, 0.96)'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(74, 134, 255, 0.96)'
                                }
                            },
                            '& .MuiInputBase-input': {
                                color: '#edf3ff',
                                fontSize: '0.98rem',
                                fontWeight: 600
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon sx={{ color: '#a8b4cd', fontSize: 24 }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={favoriteKeySet.size > 0 || favoritesOnly ? <FavoriteRoundedIcon sx={{ color: favoritesOnly ? '#ff8ea0' : '#ffffff' }} /> : <FavoriteBorderRoundedIcon />}
                        onClick={() => setFavoritesOnly(current => !current)}
                        sx={{
                            minWidth: 130,
                            minHeight: 42,
                            borderRadius: '10px',
                            borderColor: favoritesOnly ? 'rgba(255, 113, 141, 0.72)' : 'rgba(112, 136, 174, 0.34)',
                            backgroundColor: favoritesOnly ? 'rgba(255, 113, 141, 0.14)' : 'rgba(42, 54, 78, 0.56)',
                            color: '#f3f6ff',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.96rem',
                            '&:hover': {
                                borderColor: favoritesOnly ? 'rgba(255, 113, 141, 0.82)' : 'rgba(112, 136, 174, 0.5)',
                                backgroundColor: favoritesOnly ? 'rgba(255, 113, 141, 0.2)' : 'rgba(50, 63, 88, 0.7)'
                            }
                        }}
                    >
                        {t('Favorites')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CloseRoundedIcon />}
                        onClick={() => {
                            setQuery('')
                            setFilterValue('all')
                            setFavoritesOnly(false)
                        }}
                        sx={{
                            minWidth: 110,
                            minHeight: 42,
                            borderRadius: '10px',
                            backgroundColor: '#ff3345',
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 800,
                            fontSize: '0.96rem',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#ff4858',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {t('Clear')}
                    </Button>
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.55 }}>
                    <Stack direction="row" spacing={0.85}>
                        <PickerFilterButton icon={<FilterAltRoundedIcon sx={{ fontSize: 19 }} />} label={t('All')} count={counts.all} active={filterValue === 'all'} onClick={() => setFilterValue('all')} />
                        <PickerFilterButton icon={<HandymanRoundedIcon sx={{ fontSize: 19 }} />} label={t('Tools')} count={counts.tools} active={filterValue === 'tools'} onClick={() => setFilterValue('tools')} />
                    </Stack>
                    <Typography sx={{ color: '#b3bfd7', fontWeight: 700, fontSize: '0.98rem' }}>
                        {`${filteredEntries.length} ${t('results')}`}
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        borderRadius: '12px',
                        border: '1px solid rgba(123, 147, 186, 0.28)',
                        backgroundColor: 'rgba(31, 43, 67, 0.92)',
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}
                >
                    {filteredEntries.length > 0 ? filteredEntries.map((entry, index) => {
                        const isFavorite = favoriteKeySet.has(entry.key)

                        return (
                            <Box
                                key={entry.key}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    onSelect?.(entry)
                                    onClose?.()
                                }}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        onSelect?.(entry)
                                        onClose?.()
                                    }
                                }}
                                sx={{
                                    px: 2.15,
                                    py: 1.6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1.2,
                                    cursor: 'pointer',
                                    borderBottom: index < filteredEntries.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.03)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                                    <UnitIconFrame iconUrl={entry.iconUrl} itemType="tool" size={52} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                mb: 0.6,
                                                px: 0.95,
                                                minHeight: 28,
                                                width: 'fit-content',
                                                borderRadius: '10px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '1px solid rgba(130, 147, 177, 0.45)',
                                                color: '#d6deec',
                                                fontWeight: 700,
                                                fontSize: '0.94rem'
                                            }}
                                        >
                                            {t('Tool')}
                                        </Box>

                                        <Typography sx={{ color: '#f5f7ff', fontWeight: 700, fontSize: '1.14rem', lineHeight: 1.18 }}>
                                            {entry.label}
                                        </Typography>
                                    </Box>
                                </Box>

                                <IconButton
                                    onClick={event => {
                                        event.stopPropagation()
                                        toggleFavorite(entry.key)
                                    }}
                                    sx={{ color: isFavorite ? '#ff6e82' : '#9aa6bf', width: 34, height: 34 }}
                                >
                                    {isFavorite ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                                </IconButton>
                            </Box>
                        )
                    }) : (
                        <Box sx={{ px: 2.2, py: 3.2 }}>
                            <Typography sx={{ color: '#b1bdd5', fontWeight: 600 }}>
                                {t('No results found')}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        mt: 1.45,
                        px: 2.2,
                        py: 1.45,
                        mx: -2.2,
                        borderTop: '1px solid rgba(255,255,255,0.08)'
                    }}
                >
                    <Typography sx={{ color: '#7f8aa3', fontSize: '0.94rem' }}>
                        {t('Click to select - Favorite to bookmark')}
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function SlotEditorDialog({
    open,
    slotLabel,
    pickerEntries,
    value,
    onClose,
    onRemove,
    onSave,
    appLanguage = 'en'
}) {
    const t = useAutoTranslate(appLanguage, [
        'Edit Slot',
        'Pick tool',
        'Open tool picker',
        'Enter tool name or type',
        'Amount',
        'Cancel',
        'Remove',
        'Save'
    ])
    const [draftTool, setDraftTool] = React.useState(value?.tool ?? '')
    const [draftAmount, setDraftAmount] = React.useState(value?.amount ?? DEFAULT_AMOUNT_PER_SLOT)
    const [pickerOpen, setPickerOpen] = React.useState(false)

    React.useEffect(() => {
        if (!open)
            return

        setDraftTool(String(value?.tool ?? ''))
        setDraftAmount(normalizePositiveNumberText(value?.amount, DEFAULT_AMOUNT_PER_SLOT))
    }, [open, value?.amount, value?.tool])

    const savedToolValue = String(value?.tool ?? '').trim()
    const extendedPickerEntries = React.useMemo(
        () => withSavedToolFallback(pickerEntries, savedToolValue),
        [pickerEntries, savedToolValue]
    )

    const selectedEntry = React.useMemo(
        () => resolveToolSelection(draftTool, extendedPickerEntries, savedToolValue),
        [draftTool, extendedPickerEntries, savedToolValue]
    )
    const resolvedDraftToolValue = selectedEntry?.value ?? ''

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: 'min(calc(100vw - 32px), 312px)',
                        maxWidth: '312px',
                        minHeight: 208,
                        borderRadius: '12px',
                        background: 'linear-gradient(180deg, rgba(23,34,56,0.98) 0%, rgba(17,26,45,0.98) 100%)',
                        border: '1px solid rgba(125, 150, 190, 0.25)',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <DialogTitle sx={{ px: 2.25, pt: 2, pb: 1.35 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Typography sx={{ fontSize: '1.1rem', lineHeight: 1.15, fontWeight: 800 }}>
                            {t('Edit Slot')}
                        </Typography>
                        <Typography sx={{ color: '#95a5c4', fontSize: '0.83rem', fontWeight: 700 }}>
                            {slotLabel}
                        </Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ px: 2.25, pt: 0, pb: 1.25, display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
                    <TextField
                        fullWidth
                        placeholder={pickerEntries.length > 0 ? t('Pick tool') : t('Enter tool name or type')}
                        value={draftTool}
                        onChange={event => setDraftTool(event.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                minHeight: 40,
                                borderRadius: '8px',
                                backgroundColor: 'rgba(17, 26, 44, 0.92)',
                                '& fieldset': {
                                    borderColor: 'rgba(255,255,255,0.14)'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(255,255,255,0.2)'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(89, 136, 220, 0.95)'
                                }
                            },
                            '& .MuiInputBase-input': {
                                color: '#dbe7ff',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                py: 0.9
                            }
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" sx={{ mr: 0 }}>
                                    <IconButton
                                        aria-label={t('Open tool picker')}
                                        onClick={() => setPickerOpen(true)}
                                        edge="end"
                                        sx={{ color: '#edf3ff' }}
                                    >
                                        <SearchRoundedIcon sx={{ fontSize: 19 }} />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {selectedEntry ? (
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} useFlexGap flexWrap="wrap">
                            <Chip label={t('Tool')} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.12)', color: '#c3d2eb' }} />
                            <Chip label={`#${selectedEntry.id}`} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.12)', color: '#8fa3c8' }} />
                        </Stack>
                    ) : null}

                    <TextField
                        fullWidth
                        placeholder={t('Amount')}
                        value={draftAmount}
                        onChange={event => setDraftAmount(event.target.value.replace(/[^\d]/g, ''))}
                        inputProps={{ inputMode: 'numeric' }}
                        sx={{
                            mt: 1.2,
                            '& .MuiOutlinedInput-root': {
                                minHeight: 40,
                                borderRadius: '8px',
                                backgroundColor: 'rgba(64, 82, 113, 0.66)',
                                '& fieldset': {
                                    borderColor: 'rgba(255,255,255,0.06)'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(255,255,255,0.1)'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(89, 136, 220, 0.95)'
                                }
                            },
                            '& .MuiInputBase-input': {
                                color: '#dbe7ff',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                py: 0.9
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 2.25, pb: 1.9, pt: 0.6, justifyContent: 'space-between', gap: 1 }}>
                    <TextButton onClick={onClose}>
                        {t('Cancel')}
                    </TextButton>
                    <TextButton color="error" variant="contained" onClick={onRemove}>
                        {t('Remove')}
                    </TextButton>
                    <TextButton
                        variant="contained"
                        onClick={() => onSave({
                            tool: resolvedDraftToolValue,
                            amount: normalizePositiveNumberText(draftAmount, DEFAULT_AMOUNT_PER_SLOT)
                        })}
                        disabled={!resolvedDraftToolValue}
                    >
                        {t('Save')}
                    </TextButton>
                </DialogActions>
            </Dialog>

            <ToolPickerDialog
                open={pickerOpen}
                entries={extendedPickerEntries}
                onClose={() => setPickerOpen(false)}
                onSelect={entry => setDraftTool(String(entry?.id ?? entry?.value ?? ''))}
                appLanguage={appLanguage}
            />
        </>
    )
}

function TextButton({ children, color, variant = 'outlined', ...props }) {
    return (
        <Button
            {...props}
            disableElevation
            sx={{
                minWidth: 0,
                flex: 1,
                minHeight: 36,
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: 700,
                color:
                    variant === 'contained' && color === 'error' ? '#ffffff' :
                        variant === 'contained' ? '#ffffff' :
                            '#f3f7ff',
                backgroundColor:
                    variant === 'contained' && color === 'error' ? '#ff3646' :
                        variant === 'contained' ? 'rgba(77, 97, 129, 0.9)' :
                            'transparent',
                border:
                    variant === 'contained' ? 'none' :
                        '1px solid rgba(129, 146, 178, 0.26)',
                '&:hover': {
                    backgroundColor:
                        variant === 'contained' && color === 'error' ? '#ff2234' :
                            variant === 'contained' ? 'rgba(89, 110, 143, 0.95)' :
                                'rgba(255,255,255,0.04)'
                }
            }}
        >
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>{children}</Typography>
        </Button>
    )
}

export default function ToolBuildSettingsPanel({ userPlugins, onChange, plugins, appLanguage = 'en', hideHeader = false }) {
    const [toolIconCatalogById, setToolIconCatalogById] = React.useState(() => new Map())
    const pluginState = React.useMemo(
        () => userPlugins?.[PLUGIN_KEY] && typeof userPlugins[PLUGIN_KEY] === 'object' ? userPlugins[PLUGIN_KEY] : {},
        [userPlugins]
    )
    const pluginDefinition = React.useMemo(
        () => (Array.isArray(plugins) ? plugins : []).find(plugin => plugin?.key === PLUGIN_KEY) ?? null,
        [plugins]
    )
    const rawToolPickerEntriesByWorkshop = React.useMemo(
        () => buildToolPickerEntriesByWorkshop(pluginDefinition),
        [pluginDefinition]
    )
    const toolPickerEntriesByWorkshop = React.useMemo(
        () => Object.fromEntries(
            WORKSHOP_DEFINITIONS.map(workshop => [
                workshop.key,
                (rawToolPickerEntriesByWorkshop[workshop.key] ?? []).map(option => ({
                    ...option,
                    ...(toolIconCatalogById.get(String(option.value ?? option.id ?? '')) ?? {})
                }))
            ])
        ),
        [rawToolPickerEntriesByWorkshop, toolIconCatalogById]
    )

    React.useEffect(() => {
        let cancelled = false

        fetch(`/attack-template-catalog.json?language=${encodeURIComponent(appLanguage || 'en')}`)
            .then(response => response.ok ? response.json() : {})
            .then(payload => {
                if (cancelled)
                    return

                const toolEntries = [...(payload?.attackTools ?? []), ...(payload?.defenseTools ?? [])]
                const nextCatalog = new Map()
                toolEntries.forEach(option => {
                    const iconInfo = {
                        iconUrl: String(option?.iconUrl ?? ''),
                        assetKey: String(option?.assetKey ?? '')
                    }

                    ;[
                        option?.value,
                        option?.unitType
                    ]
                        .map(value => String(value ?? '').trim())
                        .filter(Boolean)
                        .forEach(value => {
                            if (!nextCatalog.has(value) || iconInfo.iconUrl)
                                nextCatalog.set(value, iconInfo)
                        })
                })
                setToolIconCatalogById(nextCatalog)
            })
            .catch(() => {
                if (!cancelled)
                    setToolIconCatalogById(new Map())
            })

        return () => {
            cancelled = true
        }
    }, [appLanguage])

    const toolOptionsByWorkshop = React.useMemo(
        () => Object.fromEntries(
            WORKSHOP_DEFINITIONS.map(workshop => {
                return [
                    workshop.key,
                    (toolPickerEntriesByWorkshop[workshop.key] ?? []).map(option => ({
                        ...option,
                        value: option.id
                    }))
                ]
            })
        ),
        [toolPickerEntriesByWorkshop]
    )

    const t = useAutoTranslate(appLanguage, [
        'Tool Build Settings',
        'Configure automatic tool building queues for siege and defense workshops in every kingdom.',
        'Each kingdom can use its own siege and defense queue order.',
        'General Rules',
        'Check Interval (Minutes)',
        'How often the module checks workshop queues.',
        'Ask Alliance Help',
        'Request alliance help after queueing tools successfully.',
        'Enable Kingdom',
        'Enable these tool queues for this kingdom.',
        'Pick tool',
        'Tool',
        'Qty',
        'Enable Queue',
        'Queue Tool',
        'Select the tool and amount used for this queue slot.',
        'Enable this slot when workshop queue space becomes available.',
        'Search by tool name...',
        ...TOOL_BUILD_KINGDOM_DEFINITIONS.flatMap(kingdom => [kingdom.title, kingdom.shortLabel, kingdom.description]),
        ...WORKSHOP_DEFINITIONS.flatMap(workshop => [workshop.title, workshop.description, workshop.shortLabel]),
        ...TOOL_BUILD_KINGDOM_DEFINITIONS.flatMap(kingdom =>
            WORKSHOP_DEFINITIONS.flatMap(workshop =>
                Array.from({ length: SLOT_COUNT }, (_, index) => `${kingdom.shortLabel} ${workshop.shortLabel} Slot ${index + 1}`)
            )
        ),
        ...Object.values(toolOptionsByWorkshop).flatMap(options => options.map(option => option.label))
    ])

    const config = React.useMemo(() => {
        const legacySlotsByWorkshop = Object.fromEntries(
            WORKSHOP_DEFINITIONS.map(workshop => [
                workshop.key,
                readLegacyWorkshopSlots(pluginState, workshop.key)
            ])
        )

        return {
            intervalMinutes: normalizePositiveNumberText(pluginState?.intervalMinutes, DEFAULT_INTERVAL_MINUTES),
            askAllianceHelp: typeof pluginState?.askAllianceHelp === 'boolean' ? pluginState.askAllianceHelp : true,
            kingdoms: Object.fromEntries(
                TOOL_BUILD_KINGDOM_DEFINITIONS.map(kingdom => {
                    const kingdomState = pluginState?.kingdoms?.[kingdom.key]
                    const enabled = typeof kingdomState?.enabled === 'boolean' ? kingdomState.enabled : true

                    return [
                        kingdom.key,
                        {
                            enabled,
                            workshops: Object.fromEntries(
                                WORKSHOP_DEFINITIONS.map(workshop => [
                                    workshop.key,
                                    {
                                        slots: readKingdomWorkshopSlots(
                                            pluginState,
                                            kingdom.key,
                                            workshop.key,
                                            legacySlotsByWorkshop
                                        )
                                    }
                                ])
                            )
                        }
                    ]
                })
            )
        }
    }, [pluginState])

    const commit = React.useCallback(nextConfig => {
        onChange(buildToolBuildPluginState(userPlugins, nextConfig))
    }, [onChange, userPlugins])

    const update = updater => {
        const nextConfig = cloneValue(config)
        updater(nextConfig)
        commit(nextConfig)
    }

    const [editorState, setEditorState] = React.useState({
        open: false,
        kingdomKey: TOOL_BUILD_KINGDOM_DEFINITIONS[0].key,
        workshopKey: WORKSHOP_DEFINITIONS[0].key,
        slotIndex: 1
    })

    const activeSlot = config.kingdoms[editorState.kingdomKey]?.workshops?.[editorState.workshopKey]?.slots?.[editorState.slotIndex - 1] ?? {
        enabled: false,
        tool: '',
        amount: DEFAULT_AMOUNT_PER_SLOT
    }
    const activeKingdom = TOOL_BUILD_KINGDOM_DEFINITIONS.find(kingdom => kingdom.key === editorState.kingdomKey)
    const activeWorkshop = WORKSHOP_DEFINITIONS.find(workshop => workshop.key === editorState.workshopKey)

    return (
        <Paper
            sx={{
                p: { xs: 2.2, md: 3.1 },
                borderRadius: '22px',
                border: '1px solid rgba(129, 146, 178, 0.18)',
                background: 'linear-gradient(180deg, rgba(27, 40, 63, 0.9) 0%, rgba(24, 35, 56, 0.86) 100%)',
                boxShadow: '0 22px 46px rgba(7, 13, 26, 0.22)'
            }}
        >
            <Stack spacing={3.2}>
                <Box sx={PANEL_CARD_SX}>
                    <Stack spacing={3}>
                        {!hideHeader && (
                            <Box>
                                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 0.55 }}>
                                    <BuildRoundedIcon sx={{ color: '#f5f7ff', fontSize: 22 }} />
                                    <Typography sx={{ color: '#f5f7ff', fontSize: '1.42rem', fontWeight: 800 }}>
                                        {t('Tool Build Settings')}
                                    </Typography>
                                </Stack>
                                <Typography sx={{ color: '#9fb0cc', fontSize: '1.02rem' }}>
                                    {t('Configure automatic tool building queues for siege and defense workshops in every kingdom.')}
                                </Typography>
                                <Helper>{t('Each kingdom can use its own siege and defense queue order.')}</Helper>
                            </Box>
                        )}

                        <Box>
                            <SectionHeading>{t('General Rules')}</SectionHeading>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                                    columnGap: { xs: 0, xl: 4.5 },
                                    rowGap: 2.45
                                }}
                            >
                                <Box>
                                    <FieldTitle>{t('Check Interval (Minutes)')}</FieldTitle>
                                    <TextField
                                        fullWidth
                                        value={config.intervalMinutes}
                                        onChange={event => update(next => { next.intervalMinutes = event.target.value })}
                                        sx={FIELD_INPUT_SX}
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                    />
                                    <Helper>{t('How often the module checks workshop queues.')}</Helper>
                                </Box>

                                <ToggleField
                                    checked={config.askAllianceHelp}
                                    onChange={value => update(next => { next.askAllianceHelp = value })}
                                    label={t('Ask Alliance Help')}
                                    helper={t('Request alliance help after queueing tools successfully.')}
                                />
                            </Box>
                        </Box>
                    </Stack>
                </Box>

                {TOOL_BUILD_KINGDOM_DEFINITIONS.map(kingdom => {
                    const kingdomConfig = config.kingdoms[kingdom.key]

                    return (
                        <Box key={kingdom.key} sx={PANEL_CARD_SX}>
                            <Stack spacing={3}>
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={2.2}
                                    alignItems={{ xs: 'stretch', md: 'flex-start' }}
                                    justifyContent="space-between"
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ color: '#f5f7ff', fontSize: '1.22rem', fontWeight: 800, mb: 0.45 }}>
                                            {t(kingdom.title)}
                                        </Typography>
                                        <Typography sx={{ color: '#95a5c4', fontSize: '0.94rem', lineHeight: 1.45 }}>
                                            {t(kingdom.description)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ minWidth: { xs: 0, md: 276 }, flexShrink: 0 }}>
                                        <ToggleField
                                            checked={kingdomConfig.enabled}
                                            onChange={value => update(next => { next.kingdoms[kingdom.key].enabled = value })}
                                            label={`${t(kingdom.shortLabel)}: ${t('Enable Kingdom')}`}
                                            helper={t('Enable these tool queues for this kingdom.')}
                                        />
                                    </Box>
                                </Stack>

                                {WORKSHOP_DEFINITIONS.map(workshop => {
                                    const WorkshopIcon = workshop.icon

                                    return (
                                        <Box
                                            key={`${kingdom.key}-${workshop.key}`}
                                            sx={{
                                                pt: 2.4,
                                                borderTop: '1px solid rgba(129, 146, 178, 0.16)'
                                            }}
                                        >
                                            <Stack spacing={2.9}>
                                                <Box>
                                                    <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 0.45 }}>
                                                        <WorkshopIcon sx={{ color: '#f5f7ff', fontSize: 20 }} />
                                                        <Typography sx={{ color: '#f5f7ff', fontSize: '1.16rem', fontWeight: 800 }}>
                                                            {t(workshop.title)}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography sx={{ color: '#95a5c4', fontSize: '0.94rem', lineHeight: 1.45 }}>
                                                        {t(workshop.description)}
                                                    </Typography>
                                                </Box>

                                                {kingdomConfig.workshops[workshop.key].slots.map((slot, index) => {
                                                    const slotIndex = index + 1
                                                    const slotLabel = t(`${kingdom.shortLabel} ${workshop.shortLabel} Slot ${slotIndex}`)
                                                    const selectedOption = (toolOptionsByWorkshop[workshop.key] ?? []).find(option => option.value === slot.tool) ??
                                                        (slot.tool ? buildFallbackOption(slot.tool) : null)

                                                    return (
                                                        <Box key={`${kingdom.key}-${workshop.key}-slot-${slotIndex}`}>
                                                            <SectionHeading>{slotLabel}</SectionHeading>

                                                            <Box
                                                                sx={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 0.9fr) minmax(280px, 1.1fr)' },
                                                                    columnGap: { xs: 0, xl: 4.5 },
                                                                    rowGap: 2.15
                                                                }}
                                                            >
                                                                <ToggleField
                                                                    checked={slot.enabled}
                                                                    onChange={value => {
                                                                        const nextSlot = getNextToolSlotForToggle(slot, value)

                                                                        if (nextSlot) {
                                                                            update(next => {
                                                                                const currentSlot = next.kingdoms[kingdom.key].workshops[workshop.key].slots[index]
                                                                                Object.assign(currentSlot, nextSlot)
                                                                            })
                                                                            return
                                                                        }

                                                                        setEditorState({ open: true, kingdomKey: kingdom.key, workshopKey: workshop.key, slotIndex })
                                                                    }}
                                                                    label={`${slotLabel}: ${t('Enable Queue')}`}
                                                                    helper={t('Enable this slot when workshop queue space becomes available.')}
                                                                    disabled={!kingdomConfig.enabled}
                                                                />

                                                                <QueueSlotField
                                                                    label={`${slotLabel}: ${t('Queue Tool')}`}
                                                                    helper={t('Select the tool and amount used for this queue slot.')}
                                                                    selectedOption={selectedOption}
                                                                    selectedLabel={selectedOption ? t(selectedOption.label) : ''}
                                                                    amount={slot.amount}
                                                                    onOpen={() => setEditorState({ open: true, kingdomKey: kingdom.key, workshopKey: workshop.key, slotIndex })}
                                                                    disabled={!kingdomConfig.enabled}
                                                                    t={t}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    )
                                                })}
                                            </Stack>
                                        </Box>
                                    )
                                })}
                            </Stack>
                        </Box>
                    )
                })}
            </Stack>

            <SlotEditorDialog
                open={editorState.open}
                slotLabel={t(`${activeKingdom?.shortLabel ?? ''} ${activeWorkshop?.shortLabel ?? ''} Slot ${editorState.slotIndex}`)}
                pickerEntries={toolPickerEntriesByWorkshop[editorState.workshopKey] ?? []}
                value={activeSlot}
                appLanguage={appLanguage}
                onClose={() => setEditorState(current => ({ ...current, open: false }))}
                onRemove={() => {
                    update(next => {
                        const targetSlot = next.kingdoms[editorState.kingdomKey].workshops[editorState.workshopKey].slots[editorState.slotIndex - 1]
                        targetSlot.enabled = false
                        targetSlot.tool = ''
                    })
                    setEditorState(current => ({ ...current, open: false }))
                }}
                onSave={nextValue => {
                    update(next => {
                        const targetSlot = next.kingdoms[editorState.kingdomKey].workshops[editorState.workshopKey].slots[editorState.slotIndex - 1]
                        targetSlot.enabled = Boolean(nextValue.tool)
                        targetSlot.tool = String(nextValue.tool ?? '')
                        targetSlot.amount = normalizePositiveNumberText(nextValue.amount, DEFAULT_AMOUNT_PER_SLOT)
                    })
                    setEditorState(current => ({ ...current, open: false }))
                }}
            />
        </Paper>
    )
}
